import React, { useState } from 'react';
import { Table, Button, Space, Tag, Typography, Input, Popconfirm, Select, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useDevices, useDeleteDevice } from '@/hooks/useDevices';
import type { Device } from '@/api/devices';
import { extractErrorMessage } from '@/utils/helpers';

const { Title } = Typography;

const DeviceList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  
  const { data, isLoading } = useDevices({ search, page, status: statusFilter });
  const deleteDevice = useDeleteDevice();

  const handleDelete = async (id: string) => {
    try {
      await deleteDevice.mutateAsync(id);
      message.success('Dispositivo excluído com sucesso!');
    } catch (error: any) {
      message.error(extractErrorMessage(error, 'Erro ao excluir dispositivo'));
    }
  };

  const columns: ColumnsType<Device> = [
    {
      title: 'Hostname',
      dataIndex: 'hostname',
      key: 'hostname',
    },
    {
      title: 'ID RustDesk',
      dataIndex: 'rustdesk_id',
      key: 'rustdesk_id',
    },
    {
      title: 'Empresa',
      dataIndex: 'company_name',
      key: 'company_name',
    },
    {
      title: 'Sistema',
      dataIndex: 'os_type',
      key: 'os_type',
      render: (os: string) => (
        <Tag color={
          os === 'windows' ? 'blue' :
          os === 'macos' ? 'purple' : 'green'
        }>
          {os === 'windows' ? 'Windows' :
           os === 'macos' ? 'macOS' : 'Linux'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={
          status === 'ONLINE' ? 'green' :
          status === 'OFFLINE' ? 'red' : 'orange'
        }>
          {status === 'ONLINE' ? 'Online' :
           status === 'OFFLINE' ? 'Offline' : 'Manutenção'}
        </Tag>
      ),
    },
    {
      title: 'Último Visto',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (date: string) => date ? new Date(date).toLocaleString('pt-BR') : '-',
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/devices/${record.id}/edit`)}
          />
          <Popconfirm
            title="Tem certeza que deseja excluir este dispositivo?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>Dispositivos</Title>
        <Space>
          <Input
            placeholder="Buscar dispositivos..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 130 }}
            allowClear
          >
            <Select.Option value="">Todos</Select.Option>
            <Select.Option value="ONLINE">Online</Select.Option>
            <Select.Option value="OFFLINE">Offline</Select.Option>
            <Select.Option value="MAINTENANCE">Manutenção</Select.Option>
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/devices/new')}
          >
            Novo Dispositivo
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data?.results || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: page,
          total: data?.count || 0,
          pageSize: 20,
          onChange: setPage,
          showTotal: (total) => `Total: ${total} dispositivos`,
        }}
      />
    </div>
  );
};

export default DeviceList;
